/**
 * Learn more drawer router.
 *
 * The "Learn more" button lives inside the inline PDP hotspot row (blocks/_product-hotspot.liquid)
 * and the runtime-cloned shop-the-look products drawer (snippets/shop-the-look-templates.liquid).
 * Both carry `data-hotspot-handle` — the handle of the hotspot metaobject they belong to. On
 * click we find the matching `<dialog-component class="learn-more-drawer" data-linked-hotspot="...">`
 * (rendered by blocks/_learn-more-drawer.liquid) and open it stacked on top of the products drawer.
 *
 * Stacking: DialogComponent detects the existing scroll-lock from the products drawer
 * and treats this as a nested dialog (it does not re-apply/un-apply the lock), so the
 * products drawer stays open behind it. We set `data-stacked` to suppress this drawer's
 * own backdrop and avoid double-darkening.
 *
 * No custom element is defined here — the block reuses the global `dialog-component`
 * (assets/dialog.js), exactly like blocks/popup-link.liquid.
 */

const LEARN_MORE_BUTTON_SELECTOR = '.shop-the-look-products-drawer__learn-more-button';
const DRAWER_SELECTOR = 'dialog-component.learn-more-drawer';

/**
 * @param {string} handle
 * @returns {Element | null}
 */
function findDrawer(handle) {
  if (!handle) return null;
  const escaped = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(handle) : handle;
  return document.querySelector(`${DRAWER_SELECTOR}[data-linked-hotspot="${escaped}"]`);
}

/**
 * @param {Element | null} drawer
 */
function openDrawer(drawer) {
  const typed = /** @type {{ showDialog?: () => void } | null} */ (drawer);
  if (!typed || typeof typed.showDialog !== 'function') return;
  // Suppress the stacked drawer's own backdrop — the products drawer behind it already
  // dims the page (mirrors mini-pdp-drawer's data-stacked behavior).
  drawer?.querySelector('dialog')?.setAttribute('data-stacked', '');
  typed.showDialog();
}

/**
 * @param {Event} event
 */
function handleClick(event) {
  const target = event.target instanceof Element ? event.target : null;
  const button = target?.closest(LEARN_MORE_BUTTON_SELECTOR);
  if (!(button instanceof HTMLElement)) return;

  const handle = button.dataset.hotspotHandle;
  if (!handle) return;

  const drawer = findDrawer(handle);
  if (!drawer) return;

  openDrawer(drawer);
}

// Guard against double-binding if the module is re-evaluated (e.g. Section Rendering API).
if (!window.__learnMoreDrawerWired) {
  window.__learnMoreDrawerWired = true;
  document.addEventListener('click', handleClick);

  // Theme editor: open/close the drawer when its block (or an ancestor block/section) is
  // selected so merchants can preview and configure it (the drawer has no on-page trigger
  // of its own).
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:block:select', (event) => {
      const scope = event.target instanceof Element ? event.target : null;
      openDrawer(scope?.querySelector(DRAWER_SELECTOR) ?? null);
    });
    document.addEventListener('shopify:block:deselect', (event) => {
      const scope = event.target instanceof Element ? event.target : null;
      const drawer = /** @type {{ closeDialog?: () => void } | null} */ (
        scope?.querySelector(DRAWER_SELECTOR) ?? null
      );
      if (drawer && typeof drawer.closeDialog === 'function') drawer.closeDialog();
    });
  }
}
